import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Play, Pause, Heart, MessageCircle, Share2, TrendingUp, Sparkles, Music, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 5: MOTION
 * Style: Dynamic, Energetic, Youth-focused
 * Best for: Gen-Z fashion, Streetwear, Trendy brands, Social-first brands
 * Inspired by: Shopify Motion theme, TikTok aesthetics
 */
const Template5 = () => {
    const navigate = useNavigate();
    const [isMuted, setIsMuted] = useState(true);
    const [activeVideo, setActiveVideo] = useState(0);
    const videoRef = useRef(null);

    // Marquee text for the scrolling banner
    const marqueeText = "NEW DROPS EVERY FRIDAY • FREE SHIPPING WORLDWIDE • TRENDING NOW • ";

    return (
        <div className="min-h-screen bg-black text-white overflow-x-hidden">

            {/* ============================================
                MARQUEE BANNER - Scrolling text animation
            ============================================ */}
            <div className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 py-2 overflow-hidden">
                <div className="animate-marquee whitespace-nowrap flex">
                    {[...Array(4)].map((_, i) => (
                        <span key={i} className="text-sm font-bold uppercase tracking-wider mx-4">
                            {marqueeText}
                        </span>
                    ))}
                </div>
            </div>

            {/* ============================================
                HERO SECTION - Full-screen video/image with gradient
            ============================================ */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                {/* Background Video/Image */}
                <div className="absolute inset-0">
                    <video
                        ref={videoRef}
                        autoPlay
                        loop
                        muted={isMuted}
                        playsInline
                        className="w-full h-full object-cover"
                        poster="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80"
                    >
                        <source src="https://assets.mixkit.co/videos/preview/mixkit-young-woman-looking-at-the-city-through-the-window-4813-large.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black"></div>
                </div>

                {/* Mute Toggle */}
                <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="absolute top-4 right-4 z-20 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                        <Sparkles className="w-4 h-4 text-yellow-400" />
                        <span className="text-sm font-medium">New Collection Dropped</span>
                    </div>
                    <h1 className="text-5xl sm:text-7xl lg:text-9xl font-black uppercase leading-none mb-6 bg-gradient-to-r from-white via-fuchsia-200 to-violet-200 bg-clip-text text-transparent">
                        BE BOLD
                    </h1>
                    <p className="text-xl sm:text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
                        Fashion that moves with you. Express yourself, break the rules.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={() => navigate('/template5/shop')}
                            className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-bold uppercase tracking-wide hover:bg-fuchsia-500 hover:text-white transition-all duration-300"
                        >
                            Shop Now
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={() => navigate('/template5/shop')}
                            className="inline-flex items-center gap-3 border-2 border-white/50 text-white px-8 py-4 font-bold uppercase tracking-wide hover:bg-white hover:text-black transition-all duration-300"
                        >
                            <Play className="w-5 h-5" fill="currentColor" />
                            Watch Film
                        </button>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
                        <div className="w-1.5 h-3 bg-white rounded-full animate-pulse"></div>
                    </div>
                </div>
            </section>

            {/* ============================================
                TRENDING NOW - Horizontal scroll cards
            ============================================ */}
            <section className="py-16 sm:py-20 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-10">
                        <div className="flex items-center gap-3">
                            <TrendingUp className="w-6 h-6 text-fuchsia-500" />
                            <h2 className="text-2xl sm:text-3xl font-black uppercase">Trending Now</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template5/shop')}
                            className="text-fuchsia-500 text-sm font-bold uppercase tracking-wide hover:text-fuchsia-400 transition-colors"
                        >
                            View All
                        </button>
                    </div>

                    <div className="flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory">
                        {[
                            { name: 'Oversized Hoodie', price: '₹2,499', tag: 'HOT', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500&fit=crop', color: 'from-violet-600 to-fuchsia-600' },
                            { name: 'Cargo Pants', price: '₹3,299', tag: 'VIRAL', image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&h=500&fit=crop', color: 'from-pink-600 to-orange-500' },
                            { name: 'Graphic Tee', price: '₹1,499', tag: 'NEW', image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&h=500&fit=crop', color: 'from-cyan-500 to-blue-600' },
                            { name: 'Platform Sneakers', price: '₹4,999', tag: 'SOLD OUT', image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400&h=500&fit=crop', color: 'from-green-500 to-emerald-600' },
                            { name: 'Chain Necklace', price: '₹999', tag: 'TRENDING', image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&h=500&fit=crop', color: 'from-amber-500 to-yellow-500' }
                        ].map((item, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template5/shop')}
                                className="flex-none w-64 sm:w-72 group cursor-pointer snap-start"
                            >
                                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-4">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-0 group-hover:opacity-60 transition-opacity duration-300`}></div>

                                    {/* Tag */}
                                    <div className={`absolute top-3 left-3 bg-gradient-to-r ${item.color} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                                        {item.tag}
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="absolute bottom-3 left-3 right-3 flex justify-between opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                                        <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black hover:bg-fuchsia-500 hover:text-white transition-colors">
                                            <Heart className="w-5 h-5" />
                                        </button>
                                        <button className="flex-1 mx-2 bg-white text-black py-2 font-bold text-sm uppercase tracking-wide hover:bg-fuchsia-500 hover:text-white transition-colors rounded-full">
                                            Add to Bag
                                        </button>
                                    </div>
                                </div>
                                <h3 className="font-bold text-white mb-1 group-hover:text-fuchsia-400 transition-colors">{item.name}</h3>
                                <p className="text-white/60">{item.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                CATEGORIES - Bold grid layout
            ============================================ */}
            <section className="py-16 sm:py-20 bg-zinc-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl sm:text-4xl font-black uppercase text-center mb-12">
                        Shop By <span className="bg-gradient-to-r from-fuchsia-500 to-violet-500 bg-clip-text text-transparent">Vibe</span>
                    </h2>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {[
                            { name: 'Streetwear', count: '120+ Items', image: 'https://images.unsplash.com/photo-1523398002811-999ca8dec234?w=400&h=500&fit=crop', gradient: 'from-violet-600/80 to-transparent' },
                            { name: 'Y2K', count: '85+ Items', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400&h=500&fit=crop', gradient: 'from-pink-600/80 to-transparent' },
                            { name: 'Minimalist', count: '65+ Items', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=400&h=500&fit=crop', gradient: 'from-zinc-600/80 to-transparent' },
                            { name: 'Sporty', count: '90+ Items', image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&h=500&fit=crop', gradient: 'from-cyan-600/80 to-transparent' }
                        ].map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template5/shop')}
                                className="group relative aspect-[3/4] rounded-3xl overflow-hidden cursor-pointer"
                            >
                                <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient}`}></div>
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors"></div>
                                <div className="absolute bottom-6 left-6 right-6">
                                    <h3 className="text-2xl sm:text-3xl font-black uppercase mb-1">{category.name}</h3>
                                    <p className="text-white/70 text-sm">{category.count}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                REELS/TIKTOK STYLE - Vertical video carousel
            ============================================ */}
            <section className="py-16 sm:py-20 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-10">
                        <div className="flex items-center gap-3">
                            <Music className="w-6 h-6 text-fuchsia-500" />
                            <h2 className="text-2xl sm:text-3xl font-black uppercase">Motion Reels</h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {[
                            { views: '2.5M', likes: '120K', image: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=300&h=500&fit=crop' },
                            { views: '1.8M', likes: '95K', image: 'https://images.unsplash.com/photo-1485968579169-68d2e5d5d4a4?w=300&h=500&fit=crop' },
                            { views: '3.2M', likes: '180K', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=300&h=500&fit=crop' },
                            { views: '890K', likes: '45K', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=300&h=500&fit=crop' },
                            { views: '1.2M', likes: '67K', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=300&h=500&fit=crop' }
                        ].map((reel, index) => (
                            <div
                                key={index}
                                className="relative aspect-[9/16] rounded-2xl overflow-hidden group cursor-pointer"
                            >
                                <img
                                    src={reel.image}
                                    alt={`Reel ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

                                {/* Play Icon */}
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                        <Play className="w-8 h-8 ml-1" fill="white" />
                                    </div>
                                </div>

                                {/* Stats */}
                                <div className="absolute bottom-4 left-4 right-4">
                                    <div className="flex items-center gap-4 text-sm">
                                        <span className="flex items-center gap-1">
                                            <Play className="w-4 h-4" fill="white" /> {reel.views}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Heart className="w-4 h-4" fill="white" /> {reel.likes}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                PRODUCTS - Grid with hover effects
            ============================================ */}
            <section className="py-16 sm:py-20 bg-zinc-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-black uppercase mb-4">
                            Fresh <span className="bg-gradient-to-r from-fuchsia-500 to-violet-500 bg-clip-text text-transparent">Drops</span>
                        </h2>
                        <p className="text-white/60">New arrivals every week</p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                            { name: 'Puffer Jacket', price: '₹5,999', originalPrice: '₹7,999', image: 'https://images.unsplash.com/photo-1544022613-e87ca75a784a?w=400&h=500&fit=crop' },
                            { name: 'Wide Leg Jeans', price: '₹2,999', originalPrice: null, image: 'https://images.unsplash.com/photo-1541099649105-f69ad21f3246?w=400&h=500&fit=crop' },
                            { name: 'Crop Top', price: '₹1,299', originalPrice: '₹1,999', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=400&h=500&fit=crop' },
                            { name: 'Bucket Hat', price: '₹899', originalPrice: null, image: 'https://images.unsplash.com/photo-1556306535-0f09a537f0a3?w=400&h=500&fit=crop' },
                            { name: 'Chunky Sneakers', price: '₹4,499', originalPrice: '₹5,999', image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400&h=500&fit=crop' },
                            { name: 'Mini Backpack', price: '₹1,799', originalPrice: null, image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=500&fit=crop' },
                            { name: 'Layered Necklace', price: '₹799', originalPrice: '₹1,199', image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&h=500&fit=crop' },
                            { name: 'Track Pants', price: '₹2,499', originalPrice: null, image: 'https://images.unsplash.com/photo-1552902865-b72c031ac5ea?w=400&h=500&fit=crop' }
                        ].map((product, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template5/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-4 bg-zinc-900">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />

                                    {/* Sale Badge */}
                                    {product.originalPrice && (
                                        <div className="absolute top-3 left-3 bg-fuchsia-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                            SALE
                                        </div>
                                    )}

                                    {/* Quick Add */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                        <button className="w-full bg-white text-black py-3 font-bold text-sm uppercase tracking-wide hover:bg-fuchsia-500 hover:text-white transition-colors rounded-xl">
                                            Quick Add
                                        </button>
                                    </div>
                                </div>

                                <h3 className="font-bold text-white mb-1 group-hover:text-fuchsia-400 transition-colors">{product.name}</h3>
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-white">{product.price}</span>
                                    {product.originalPrice && (
                                        <span className="text-white/40 line-through text-sm">{product.originalPrice}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template5/shop')}
                            className="inline-flex items-center gap-3 bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white px-10 py-4 font-bold uppercase tracking-wide hover:from-fuchsia-500 hover:to-violet-500 transition-all duration-300 rounded-full"
                        >
                            View All Products
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ============================================
                USER GENERATED CONTENT - Social proof
            ============================================ */}
            <section className="py-16 sm:py-20 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-black uppercase mb-4">
                            #MotionStyle
                        </h2>
                        <p className="text-white/60">Tag us to get featured</p>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {[
                            'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=300&h=300&fit=crop',
                            'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=300&h=300&fit=crop'
                        ].map((image, index) => (
                            <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img
                                    src={image}
                                    alt={`UGC ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-fuchsia-600/0 group-hover:bg-fuchsia-600/40 transition-colors flex items-center justify-center">
                                    <div className="flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="flex items-center gap-1 text-white">
                                            <Heart className="w-5 h-5" fill="white" />
                                        </span>
                                        <span className="flex items-center gap-1 text-white">
                                            <MessageCircle className="w-5 h-5" />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============================================
                NEWSLETTER - Fun, engaging design
            ============================================ */}
            <section className="py-16 sm:py-20 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-medium">Join the Movement</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase mb-4">
                        Get 15% Off
                    </h2>
                    <p className="text-white/90 text-lg mb-8">
                        Plus early access to drops, exclusive content, and more.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="your@email.com"
                            className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors"
                        />
                        <button
                            type="submit"
                            className="px-8 py-4 bg-white text-fuchsia-600 font-bold uppercase tracking-wide rounded-full hover:bg-black hover:text-white transition-all duration-300"
                        >
                            Subscribe
                        </button>
                    </form>
                    <p className="text-white/60 text-sm mt-4">
                        No spam. Unsubscribe anytime.
                    </p>
                </div>
            </section>

            {/* ============================================
                SOCIAL LINKS - Connect section
            ============================================ */}
            <section className="py-12 bg-black border-t border-zinc-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                        <p className="text-white/60 text-sm">Follow us for daily inspo</p>
                        <div className="flex items-center gap-4">
                            {['Instagram', 'TikTok', 'YouTube', 'Twitter'].map((social) => (
                                <button
                                    key={social}
                                    className="px-4 py-2 bg-zinc-900 rounded-full text-sm font-medium text-white/80 hover:bg-fuchsia-600 hover:text-white transition-colors"
                                >
                                    {social}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Add marquee animation styles */}
            <style jsx>{`
                @keyframes marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .animate-marquee {
                    animation: marquee 20s linear infinite;
                }
            `}</style>

        </div>
    );
};

export default Template5;
